<template>
    <div class="stories-container">
        <div v-if="loading" class="loading-stories">
            <!-- Loading skeleton -->
            <div v-for="i in 5" :key="i" class="story-skeleton">
                <div class="avatar-skeleton"></div>
                <div class="username-skeleton"></div>
            </div>
        </div>
        
        <div v-else class="stories-list">
            <div 
                v-for="(stories, userId) in groupedStories" 
                :key="userId"
                class="story-item"
                @click="openStory(stories[0].user, stories)"
            >
                <div class="avatar-container" :class="{ 'has-unviewed': hasUnviewedStories(stories) }">
                    <img :src="getAvatarUrl(stories[0].user)" class="avatar" />
                </div>
                <span class="username">{{ stories[0].user.name }}</span>
            </div>
        </div>
        
        <StoryViewer 
            v-if="showViewer"
            :user="currentStoryUser"
            :stories="currentStories"
            :initialIndex="currentStoryIndex"
            @close="closeViewer"
            @view="markStoryAsViewed"
        />
    </div>
</template>

<script>
import axios from 'axios';
import StoryViewer from './StoryViewer.vue';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api',
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));

export default {
    components: {
        StoryViewer
    },
    data() {
        return {
            loading: true,
            groupedStories: {},
            viewedStories: [],
            showViewer: false,
            currentStoryUser: null,
            currentStories: [],
            currentStoryIndex: 0
        };
    },
    async created() {
        await this.fetchStories();
    },
    methods: {
        async fetchStories() {
            try {
                const response = await api.get('/stories');
                this.groupedStories = response.data.stories;
                this.viewedStories = response.data.viewed_stories;
            } catch (error) {
                console.error('Error fetching stories:', error);
            } finally {
                this.loading = false;
            }
        },
        hasUnviewedStories(stories) {
            return stories.some(story => !this.viewedStories.includes(story.id));
        },
        getAvatarUrl(user) {
            // Replace with your actual avatar URL logic
            return user.avatar_url || '/default-avatar.jpg';
        },
        openStory(user, stories) {
            this.currentStoryUser = user;
            this.currentStories = stories;
            this.currentStoryIndex = 0;
            this.showViewer = true;
            
            // Mark the first story as viewed
            if (this.hasUnviewedStories(stories)) {
                this.markStoryAsViewed(stories[0].id);
            }
        },
        closeViewer() {
            this.showViewer = false;
            this.fetchStories(); // Refresh the list
        },
        async markStoryAsViewed(storyId) {
            try {
                await api.post(`/stories/${storyId}/view`);
                if (!this.viewedStories.includes(storyId)) {
                    this.viewedStories.push(storyId);
                }
            } catch (error) {
                console.error('Error marking story as viewed:', error);
            }
        }
    }
};
</script>

<style scoped>
.stories-container {
    padding: 10px 0;
    background: white;
    border-bottom: 1px solid #eee;
}

.stories-list {
    display: flex;
    overflow-x: auto;
    padding: 0 10px;
    gap: 15px;
}

.story-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
}

.avatar-container {
    width: 70px;
    height: 70px;
    border-radius: 50%;
    padding: 3px;
    position: relative;
}

.avatar-container.has-unviewed {
    background: linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888);
}

.avatar {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 2px solid white;
    object-fit: cover;
}

.username {
    margin-top: 5px;
    font-size: 12px;
    text-align: center;
    max-width: 70px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.loading-stories {
    display: flex;
    overflow-x: auto;
    padding: 0 10px;
    gap: 15px;
}

.story-skeleton {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.avatar-skeleton {
    width: 70px;
    height: 70px;
    border-radius: 50%;
    background: #eee;
}

.username-skeleton {
    width: 50px;
    height: 10px;
    background: #eee;
    margin-top: 5px;
    border-radius: 5px;
}

/* Hide scrollbar but allow scrolling */
.stories-list::-webkit-scrollbar {
    display: none;
}

.stories-list {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
</style>